import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Chip,
    Button,
    IconButton,
    Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import NoteIcon from '@mui/icons-material/StickyNote2';
import ManageSessionDialog, { SessionData } from '../components/ManageSessionDialog';
import { collectLotKeys, sessionBelongsToLots } from '../sessionScope';

interface VendorActiveSessionsProps {
    vendorId: string;
}

export default function VendorActiveSessions({ vendorId }: VendorActiveSessionsProps) {
    const [sessions, setSessions] = useState<SessionData[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedSession, setSelectedSession] = useState<SessionData | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    const fetchActiveSessions = async () => {
        setLoading(true);
        try {
            // First get vendor's lot names
            const lotsQuery = query(
                collection(db, 'parkinglots'),
                where('vendorId', '==', vendorId)
            );
            const lotsSnapshot = await getDocs(lotsQuery);
            // Scope by lot document id, falling back to name only for
            // sessions written before lotID existed — see sessionScope.ts.
            const { lotIds, lotNames } = collectLotKeys(lotsSnapshot.docs);

            if (lotIds.size === 0) {
                setSessions([]);
                setLoading(false);
                return;
            }

            // Get all active sessions
            const sessionsQuery = query(
                collection(db, 'parkingsessions'),
                where('sessionActive', '==', true)
            );
            const sessionsSnapshot = await getDocs(sessionsQuery);

            const vendorSessions: SessionData[] = sessionsSnapshot.docs
                .map(doc => {
                    const data = doc.data();

                    const startTs = data.startTime;
                    const startDateObj =
                        startTs && typeof startTs.toDate === 'function'
                            ? startTs.toDate()
                            : null;

                    return {
                        id: doc.id,
                        sessionID: doc.id,
                        userEmail: data.customerInfo?.email ?? '',
                        lotName: data.lotName ?? '',
                        lotID: data.lotID ?? '',
                        spaceNumber: data.spaceNumber ?? '',
                        vehicle: data.vehicleInfo
                            ? {
                                licensePlate: data.vehicleInfo.licensePlate ?? '',
                                make: data.vehicleInfo.make ?? '',
                                color: data.vehicleInfo.color ?? '',
                                nickname: data.vehicleInfo.nickname ?? '',
                            }
                            : undefined,
                        duration: data.duration ?? undefined,
                        prepaidHours: typeof data.duration === 'number'
                            ? Math.ceil(data.duration / 60)
                            : undefined,
                        currency: data.currency ?? '',
                        rate: data.rate ?? undefined,
                        adminNotes: data.adminNotes ?? [],
                        sessionActive: Boolean(data.sessionActive),
                        startDate: startDateObj
                            ? startDateObj.toLocaleDateString()
                            : '',
                        startTime: startDateObj
                            ? startDateObj.toLocaleTimeString()
                            : '',
                    };
                })
                .filter(session => sessionBelongsToLots(session, lotIds, lotNames));

            setSessions(vendorSessions);
        } catch (error) {
            console.error('Error fetching sessions:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchActiveSessions();
        const interval = setInterval(fetchActiveSessions, 30000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vendorId]);

    const getTimeStatus = (session: SessionData) => {
        if (!session.startDate || !session.startTime || !session.prepaidHours) return null;
        try {
            const startDateTime = new Date(`${session.startDate} ${session.startTime}`);
            const endDateTime = new Date(startDateTime.getTime() + (session.prepaidHours * 60 * 60 * 1000));
            const diffMs = endDateTime.getTime() - Date.now();
            const diffMinutes = Math.round(diffMs / (1000 * 60));
            if (diffMinutes > 0) {
                const h = Math.floor(diffMinutes / 60);
                const m = diffMinutes % 60;
                return { label: h > 0 ? `${h}h ${m}m` : `${m}m`, color: 'success' as const };
            } else {
                const over = Math.abs(diffMinutes);
                const h = Math.floor(over / 60);
                const m = over % 60;
                return { label: h > 0 ? `-${h}h ${m}m` : `-${m}m`, color: 'error' as const };
            }
        } catch {
            return null;
        }
    };

    if (loading && sessions.length === 0) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
    }

    return (
        <Box>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: 1,
                mb: 2
            }}>
                <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                    Active Sessions
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Chip label={`${sessions.length} Active`} color="success" size="small" />
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={fetchActiveSessions}
                        size="small"
                    >
                        Refresh
                    </Button>
                </Box>
            </Box>

            {sessions.length === 0 ? (
                <Paper sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center' }}>
                    <Typography color="textSecondary">
                        No active sessions at your parking lots
                    </Typography>
                </Paper>
            ) : (
                <Box sx={{ overflowX: 'auto' }}>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 750 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>License Plate</TableCell>
                                    <TableCell>Vehicle</TableCell>
                                    <TableCell>Parking Lot</TableCell>
                                    <TableCell>Space</TableCell>
                                    <TableCell>Time Parked</TableCell>
                                    <TableCell>Prepaid</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sessions.map((session) => {
                                    const status = getTimeStatus(session);
                                    const hasNotes = session.adminNotes && session.adminNotes.length > 0;
                                    const timeParked = (() => {
                                        try {
                                            const start = new Date(`${session.startDate} ${session.startTime}`);
                                            const diffMs = Date.now() - start.getTime();
                                            const h = Math.floor(diffMs / (1000 * 60 * 60));
                                            const m = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                                            return `${h}h ${m}m`;
                                        } catch { return 'N/A'; }
                                    })();

                                    return (
                                        <React.Fragment key={session.id}>
                                            <TableRow
                                                sx={{
                                                    backgroundColor: status?.color === 'error' ? '#ffebee' : 'inherit',
                                                    '& > td': hasNotes ? { borderBottom: 'none' } : {},
                                                }}
                                            >
                                                <TableCell>
                                                    <strong>{session.vehicle?.licensePlate || 'N/A'}</strong>
                                                </TableCell>
                                                <TableCell>
                                                    {session.vehicle ? `${session.vehicle.make} - ${session.vehicle.color}` : 'N/A'}
                                                </TableCell>
                                                <TableCell>{session.lotName}</TableCell>
                                                <TableCell>{session.spaceNumber || '—'}</TableCell>
                                                <TableCell>{timeParked}</TableCell>
                                                <TableCell>{session.prepaidHours ? `${session.prepaidHours}h` : 'Hourly'}</TableCell>
                                                <TableCell>
                                                    {status ? (
                                                        <Chip label={status.label} color={status.color} size="small" />
                                                    ) : (
                                                        <Chip label="Active" color="info" size="small" />
                                                    )}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Tooltip title="Manage session">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                setSelectedSession(session);
                                                                setDialogOpen(true);
                                                            }}
                                                        >
                                                            <SettingsIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                            {hasNotes && (
                                                <TableRow sx={{ backgroundColor: status?.color === 'error' ? '#ffebee' : 'inherit' }}>
                                                    <TableCell colSpan={8} sx={{ pt: 0, pb: 1.5 }}>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: 1 }}>
                                                            {session.adminNotes!.map((n, i) => {
                                                                const ts = n.timestamp?.toDate ? n.timestamp.toDate() : (n.timestamp?.seconds ? new Date(n.timestamp.seconds * 1000) : null);
                                                                return (
                                                                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                                                        <NoteIcon sx={{ fontSize: 14, color: '#90a4ae', mt: '2px' }} />
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            <strong>{n.addedBy}</strong>
                                                                            {ts ? ` · ${ts.toLocaleDateString()} ${ts.toLocaleTimeString()}` : ''}
                                                                            {' — '}
                                                                        </Typography>
                                                                        <Typography variant="caption">{n.note}</Typography>
                                                                    </Box>
                                                                );
                                                            })}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            <ManageSessionDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                session={selectedSession}
                onSuccess={fetchActiveSessions}
            />
        </Box>
    );
}
